import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { languages_name, translateName } from '../../functions/language.functions';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { GbGemeindeSearchMode } from './gbGemeindeSearchMode';
import { Observable, Subscription } from 'rxjs';
import { GbGemeindeId, GbGemeindeListEntry, GbgemeindeService } from 'app/core/gb-api';
import { GwrGemeindeId } from 'app/core/gwr-api';
import { AppPermissionService } from '../../../core/services/app-permission.service';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { AbstractTypeaheadSearch } from '../../components/typeahead-search/abstract-typeahead-search';
import { MetaDisablerDirective } from '../../directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../directives/meta-validator.directive';

@Component({
  selector: 'cap-gb-gemeinde',
  templateUrl: './gb-gemeinde.component.html',
  styleUrls: ['./gb-gemeinde.component.scss'],
  host: {
    class: 'form-control-component'
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GbGemeindeComponent),
      multi: true
    }
  ],
  imports: [FormsModule, NgbTypeahead, MetaDisablerDirective, MetaValidatorDirective, TranslatePipe]
})
export class GbGemeindeComponent
  extends AbstractTypeaheadSearch<GbGemeindeListEntry>
  implements OnInit, AfterViewInit, ControlValueAccessor, OnDestroy
{
  @Input()
  searchMode: GbGemeindeSearchMode;

  @Input()
  nurGueltige = true;

  @Input()
  hasfocus = false;

  @Input()
  mitExternen = true;

  @Input()
  mitAufSuchmaskeVerborgenen = false;

  model: GbGemeindeListEntry;

  propagateChange: (id: GbGemeindeId) => void;
  propagateTouch: (_) => void;
  hasPermissionSeybuch: boolean;
  private reset = false;
  private gemeindeSubscription: Subscription;

  @ViewChild('searchInput', { static: false }) searchInput: ElementRef;
  private data: Observable<Array<GbGemeindeListEntry>>;

  constructor(
    private translate: TranslateService,
    private gbGemeindeService: GbgemeindeService,
    private permissionService: AppPermissionService
  ) {
    super();
  }

  ngOnInit() {
    this.checkPermissionSeybuch();
    this.data = this.getData();
  }

  private getData() {
    return this.gbGemeindeService.loadGbGemeindeListEntries(
      GbGemeindeSearchMode[this.searchMode], //
      this.nurGueltige,
      this.mitExternen,
      this.hasPermissionSeybuch,
      this.mitAufSuchmaskeVerborgenen
    );
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.hasfocus) {
        this.searchInput.nativeElement.focus();
      }
    });
  }

  writeValue(id: GbGemeindeId | GwrGemeindeId): void {
    if (id) {
      this.gemeindeSubscription = this.gbGemeindeService.loadGbGemeindeListEntryById(id.id).subscribe((gemeinde) => {
        this.value = this.formatEntry(gemeinde);
        this.selectedItem = gemeinde;
        this.model = gemeinde;
        this.propagateChange(gemeinde.id);
      });
      this.reset = false;
    } else if (!this.reset) {
      this.reset = true;
      this.onClear();
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  onBlur() {
    if (!this.selectedItem) {
      this.value = '';
      this.propagateChange(null);
    }
  }

  onSelected($event: NgbTypeaheadSelectItemEvent<GbGemeindeListEntry>) {
    const selectedItem: GbGemeindeListEntry = $event.item;
    if (selectedItem) {
      this.selectedItem = selectedItem;
      this.model = selectedItem;
      this.value = translateName(selectedItem.name, this.translate) + ' ' + selectedItem.bfsNummer;
      this.propagateChange(selectedItem.id);
      this.reset = false;
    }
  }

  onClear() {
    super.onClear();
    this.model = null;
  }

  checkPermissionSeybuch() {
    this.hasPermissionSeybuch = this.permissionService.hasPermission('SEYBUCH_REGISTER');
  }

  ngOnDestroy(): void {
    this.gemeindeSubscription?.unsubscribe();
  }

  search = (search$: Observable<string>) => {
    return search$.pipe(
      distinctUntilChanged(),
      switchMap((searchTerm) => {
        this.searchTerm = searchTerm.toLowerCase();
        if (!this.data) {
          this.data = this.getData();
        }
        return this.data;
      }),
      map((data: GbGemeindeListEntry[]) => {
        const matches = this.extractMatches(data, this.searchTerm);
        this.results = matches;
        return matches;
      })
    );
  };

  formatEntry = (entry: GbGemeindeListEntry): string => {
    const currentLanguageProperty: string = languages_name[this.translate.getCurrentLang()].name;
    let title = entry[currentLanguageProperty];
    if (entry.bfsNummer) {
      title += ' ' + entry.bfsNummer;
    }
    if (entry.kreisNummer) {
      title += '.' + entry.kreisNummer;
    }
    return title;
  };

  protected extractMatches(data: GbGemeindeListEntry[], term: string) {
    const matches = [...data].filter(this.filterResults);
    if (/^\d+$/.test(term) || /^\d{3,4}\.[\d\w]+$/.test(term)) {
      return matches.sort((a, b): number => this.sortByBfsKreisNummerOrKuerzel(a, b));
    }
    return matches.sort((a, b): number => this.sortResults(a, b));
  }

  protected filterResults = (result: GbGemeindeListEntry): boolean => {
    let title: string = this.formatEntry(result);
    if (result.kuerzel) {
      title += ' ' + result.kuerzel;
    }
    return title.toLocaleLowerCase().includes(this.searchTerm.toLowerCase());
  };

  private sortByBfsKreisNummerOrKuerzel(a: GbGemeindeListEntry, b: GbGemeindeListEntry): number {
    const bfsComparison: number = this.sortByBfsNummer(a, b);
    if (bfsComparison !== 0) {
      return bfsComparison;
    }
    const kreisComparison: number = this.sortString(a.kreisNummer, b.kreisNummer);
    if (kreisComparison !== 0) {
      return kreisComparison;
    }
    const kuerzelComparison: number = this.sortString(a.kuerzel, b.kuerzel);
    if (kuerzelComparison !== 0) {
      return kuerzelComparison;
    }
    return 0;
  }

  private sortByBfsNummer(a: GbGemeindeListEntry, b: GbGemeindeListEntry): number {
    if (a.bfsNummer < b.bfsNummer) {
      return -1;
    }
    if (a.bfsNummer > b.bfsNummer) {
      return 1;
    }
    return 0;
  }

  private sortString(a: string, b: string): number {
    if (a === null && b !== null) {
      return -1;
    }
    if (b === null && a !== null) {
      return 1;
    }
    if (a === null && b === null) {
      return 0;
    }

    const aIsNum: boolean = isNaN(parseInt(a, 10));
    const bIsNum: boolean = isNaN(parseInt(b, 10));
    if (!aIsNum && bIsNum) {
      return -1;
    }
    if (aIsNum && !bIsNum) {
      return 1;
    }
    if (aIsNum && bIsNum) {
      if (a > b) {
        return 1;
      }
      if (a < b) {
        return -1;
      }
      return 0;
    }

    if (+a > +b) {
      return 1;
    }
    if (+a < +b) {
      return -1;
    }
    return 0;
  }
}
